'use client';

import { useEffect } from 'react';

const ANIMATION_CLASSES = [
    'fa-spin',
    'fa-spin-pulse',
    'fa-spin-reverse',
    'fa-beat',
    'fa-beat-fade',
    'fa-bounce',
    'fa-fade',
    'fa-flip',
    'fa-shake',
];

const RESTART_PREFIX = 'fa-anim-restart-';

function cleanClassList(list) {
    return list.filter(
        (cls) => cls && !cls.startsWith(RESTART_PREFIX) && cls !== ''
    );
}

function restartAnimation(el) {
    let hasAnim = false;
    const hadOnce = el.classList.contains('fa-once');
    ANIMATION_CLASSES.forEach((cls) => {
        if (el.classList.contains(cls)) {
            hasAnim = true;
            el.classList.remove(cls);
            // Force reflow to restart animation
            void el.offsetWidth;
            el.classList.add(cls);
        }
    });
    if (hadOnce) {
        el.classList.remove('fa-once');
        void el.offsetWidth;
        el.classList.add('fa-once');
        hasAnim = true;
    }
    if (hasAnim) {
        const tmp = `fa-anim-restart-${Date.now()}-${Math.random()
            .toString(16)
            .slice(2)}`;
        el.classList.add(tmp);
        requestAnimationFrame(() => el.classList.remove(tmp));
    }
}

export default function IconAnimationInView() {
    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) restartAnimation(entry.target);
                });
            },
            { threshold: 0.15 }
        );

        const track = (el) => {
            if (!el || !el.classList) return;
            observer.observe(el);
        };

        const scan = () => {
            document
                .querySelectorAll('i[class*="fa-"]')
                .forEach((el) => track(el));
        };

        scan();

        const mutation = new MutationObserver((mutations) => {
            mutations.forEach((mutationRecord) => {
                mutationRecord.addedNodes.forEach((node) => {
                    if (!(node instanceof HTMLElement)) return;
                    if (node.matches('i[class*="fa-"]')) track(node);
                    node.querySelectorAll?.('i[class*="fa-"]').forEach(track);
                });
            });
        });

        mutation.observe(document.body, { childList: true, subtree: true });

        const handleManualRestart = (event) => {
            const iconName = event?.detail?.iconName;
            const cls = event?.detail?.className;
            const run = () => {
                const targets = new Set();

                if (cls) {
                    const cleaned = cleanClassList(
                        cls.split(/\s+/).filter(Boolean)
                    );
                    if (cleaned.length) {
                        const selector =
                            'i.' +
                            cleaned
                                .map((c) => c.replace(/\./g, '\\.'))
                                .join('.');
                        document
                            .querySelectorAll(selector)
                            .forEach((el) => targets.add(el));
                    }
                }

                if (iconName) {
                    document
                        .querySelectorAll(`i[class*="fa-${iconName}"]`)
                        .forEach((el) => targets.add(el));
                }

                targets.forEach((el) => restartAnimation(el));
            };

            run();
            setTimeout(run, 120);
        };

        window.addEventListener('fa-restart', handleManualRestart);

        return () => {
            observer.disconnect();
            mutation.disconnect();
            window.removeEventListener('fa-restart', handleManualRestart);
        };
    }, []);

    return null;
}
